/**
 * Parameters for getUserTypes. Pass config and me from useUDData(), headerData from headerStore.userSpecificContext.
 * No React hooks required – use the helper directly where you have header data.
 */

/**
 * UserTypes – boolean flags for subscription type and instructor status.
 * Matches V2 API surface (getUserTypes return shape).
 */

/**
 * Returns true if the given user id is in the course instructors list.
 * Used when instructors are passed to getUserTypes to set isCourseInstructor.
 */
function isUserCourseInstructor(userId, instructors) {
  if (userId == null || !(instructors !== null && instructors !== void 0 && instructors.length)) return false;
  const idStr = String(userId);
  return instructors.some(instructor => String(instructor.id) === idStr);
}

/**
 * Determines the user's subscription type from config, me, and header user context.
 * Pure helper usable in client and server-like contexts (no React hooks).
 * Matches V2 getUserTypes API surface; TP semantics in V1 are UB + Config.brand.is_team
 * until common-data is available.
 *
 * @param params - config (features, brand), me (is_authenticated, organization), headerData (user, organizationState)
 * @param instructors - Optional. When provided, isCourseInstructor is set if params.headerData.user.id is in this list.
 * @returns GetUserTypesResult – UserTypes plus isRegularUser and isSubscriptionPlanUser for backward compatibility.
 */
export function getUserTypes(params, instructors) {
  var _config$features, _me$organization, _headerData$user, _config$brand, _headerData$organizat, _headerData$user2;
  const {
    config,
    me,
    headerData
  } = params;
  const isUBUser = !!me.is_authenticated && !!me.organization;
  const isUBProUser = isUBUser && (!!((_config$features = config.features) !== null && _config$features !== void 0 && (_config$features = _config$features.organization) !== null && _config$features !== void 0 && (_config$features = _config$features.learning_path) !== null && _config$features !== void 0 && _config$features.pro_path) || !!me.is_authenticated && !!((_me$organization = me.organization) !== null && _me$organization !== void 0 && _me$organization.is_pro_license_holder));
  const isPPUser = !!((_headerData$user = headerData.user) !== null && _headerData$user !== void 0 && _headerData$user.consumer_subscription_active);
  const isTPUser = isUBUser && !!((_config$brand = config.brand) !== null && _config$brand !== void 0 && _config$brand.is_team);
  const isMXUser = !isPPUser && !isUBUser;
  const packageAlert = (_headerData$organizat = headerData.organizationState) === null || _headerData$organizat === void 0 ? void 0 : _headerData$organizat.package_alert;
  const isTPTrialUser = isUBUser && !!(packageAlert !== null && packageAlert !== void 0 && packageAlert.is_team_plan) && !!(packageAlert !== null && packageAlert !== void 0 && packageAlert.is_free_trial);
  const isPPTrialUser = false;
  const isCourseInstructor = isUserCourseInstructor((_headerData$user2 = headerData.user) === null || _headerData$user2 === void 0 ? void 0 : _headerData$user2.id, instructors);
  const isConsumerSubscriber = isPPUser || isUBUser || isTPUser;
  return {
    isUBUser,
    isUBProUser,
    isTPUser,
    isPPUser,
    isMXUser,
    isTPTrialUser,
    isPPTrialUser,
    isCourseInstructor,
    isConsumerSubscriber
  };
}
//# sourceMappingURL=get-user-types.js.map